/**
 * Membership service — fetches membership data by token from the membership API.
 */

export interface MembershipEntitlement {
  id: string;
  membership_id: string;
  entitlement_type: string; // "baseline_blood_test" | "followup_blood_test"
  stripe_product_id: string;
  status: string; // "available" | "booked" | "completed" | "expired" | "cancelled"
  source: string; // "plan" | "addon" | "gift" | "migration"
  external_booking_id: string | null;
  billing_cycle: number;
  period_start: string | null;
  period_end: string | null;
  booked_at: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
}

interface RawMembershipResponse {
  id: string;
  firebase_uid: string | null;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  plan_slug: string | null;
  status: string;
  psp_name: string | null;
  psp_uid: string | null;
  cycle_count: number;
  current_period_start: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  discount_percentage: number; // decimal, e.g. 0.05 = 5%
  discount_fixed_value: number;
  discount_code: string | null;
  original_price_chf: number | null;
  paid_price_chf: number | null;
  created_at: string;
  cancelled_at: string | null;
  tos_accepted_at: string | null;
  medical_consent_accepted_at: string | null;
  membership_entitlements: MembershipEntitlement[];
  current_available_entitlement: MembershipEntitlement | null;
  /** "membership-first" when the membership was created by the pay-first flow; absent/null for legacy flows. */
  flow?: string | null;
}

export interface MembershipData {
  id: string;
  email: string | null;
  first_name: string | null;
  last_name: string | null;
  plan_slug: string | null;
  status: string;
  discount_percentage: number;
  discount_fixed_value: number;
  membership_entitlements: MembershipEntitlement[];

  /** Discount percentage as integer (e.g. 5 means 5%). */
  membershipDiscountPercentage?: number;
  /** Fixed discount in rappen/cents. */
  membershipDiscountFixValue?: number;
  /** Stripe product IDs from *available* entitlements — used for auto-select and CHF 0 pricing. */
  entitledProductIds: string[];
  /** Server-resolved current usable entitlement (baseline before followup), or null if none left. */
  currentAvailableEntitlement: MembershipEntitlement | null;
  /** ISO timestamp when the user accepted T&C + Privacy Policy at membership signup. */
  tosAcceptedAt: string | null;
  /** ISO timestamp when the user accepted medical data consent at membership signup. */
  medicalConsentAcceptedAt: string | null;
  /** "membership-first" when the membership was created by the pay-first flow; null for legacy flows. */
  flow: string | null;
}

const STAGING_MEMBERSHIP_URL = "https://staging.membership.aheadhealth.dev";

function getMembershipBaseUrl(): string {
  if (import.meta.env.VITE_MEMBERSHIP_BASE_URL) {
    return import.meta.env.VITE_MEMBERSHIP_BASE_URL;
  }
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    if (
      hostname === "localhost" ||
      hostname === "127.0.0.1" ||
      hostname.includes(".vercel.app")
    ) {
      return STAGING_MEMBERSHIP_URL;
    }
  }
  return STAGING_MEMBERSHIP_URL;
}

const BASE_URL = getMembershipBaseUrl();

/** The membership API this build reads memberships from (a preview's is
 *  staging's): the booking says it, so the entitlement is booked there too. */
export const MEMBERSHIP_API_ORIGIN = BASE_URL;

/**
 * Fetch membership data by token.
 * Returns null on failure, invalid token, or non-active membership.
 *
 * `baseUrlOverride` lets the linking app point this fetch at a specific
 * membership API (and thus a specific database) per environment, mirroring
 * the `reportBackendDomain` override used for the activation backend. The
 * report app passes its own environment's membership-app URL as the
 * `membershipDomain` query param so a staging / preview booking session
 * resolves the token against the same database that created the membership.
 * Falls back to the build-time `VITE_MEMBERSHIP_BASE_URL` when absent.
 */
export async function fetchMembership(
  token: string,
  baseUrlOverride?: string | null,
): Promise<MembershipData | null> {
  if (!token || token.trim().length === 0) {
    return null;
  }

  const base = baseUrlOverride?.trim() || BASE_URL;

  try {
    const response = await fetch(
      `${base}/api/membership/by-token/${encodeURIComponent(token)}`,
    );

    if (!response.ok) {
      console.warn("[MembershipService] API returned status:", response.status);
      return null;
    }

    const raw: RawMembershipResponse = await response.json();

    const status = (raw.status || "").toLowerCase();
    if (status !== "active" && status !== "cancelling") {
      console.warn(
        "[MembershipService] Membership status is not active:",
        raw.status,
      );
      return null;
    }

    const entitlements = raw.membership_entitlements || [];
    const availableEntitlements = entitlements.filter(
      (e) => e.status === "available",
    );

    // Convert decimal percentage (0.05) → integer (5) for UI display
    const discountPct =
      raw.discount_percentage > 0
        ? Math.round(raw.discount_percentage * 100)
        : undefined;

    return {
      id: raw.id,
      email: raw.email,
      first_name: raw.first_name,
      last_name: raw.last_name,
      plan_slug: raw.plan_slug,
      status: raw.status,
      discount_percentage: raw.discount_percentage,
      discount_fixed_value: raw.discount_fixed_value,
      membership_entitlements: entitlements,
      membershipDiscountPercentage: discountPct,
      membershipDiscountFixValue:
        raw.discount_fixed_value > 0 ? raw.discount_fixed_value : undefined,
      entitledProductIds: availableEntitlements.map((e) => e.stripe_product_id),
      currentAvailableEntitlement: raw.current_available_entitlement ?? null,
      tosAcceptedAt: raw.tos_accepted_at ?? null,
      medicalConsentAcceptedAt: raw.medical_consent_accepted_at ?? null,
      flow: raw.flow ?? null,
    };
  } catch (err) {
    console.error("[MembershipService] Fetch failed:", err);
    return null;
  }
}

/**
 * Resume state for a membership token. Unlike fetchMembership (which returns
 * null for non-active statuses and non-2xx responses), this function returns
 * a discriminated union so the caller can route to the right screen:
 *   - "not_found": 404 or invalid token
 *   - "inactive": membership exists but is cancelled or another non-active status
 *   - "pending": the membership exists but status is "pending"/"incomplete" (paid
 *     but invoice.paid not processed yet)
 *   - "unreachable": the API failed (non-2xx other than 404) or could not be
 *     reached; ask again
 *   - "active": membership is active or cancelling, data is available
 */
export type MembershipResumeState =
  | { kind: "not_found" }
  | { kind: "inactive"; status: string }
  | { kind: "pending" }
  // The API failed or could not be reached: not an answer about the
  // membership, so it is asked again rather than reported missing.
  | { kind: "unreachable" }
  | { kind: "active"; data: MembershipData };

export async function fetchMembershipResumeState(
  token: string,
  baseUrlOverride?: string | null,
): Promise<MembershipResumeState> {
  if (!token || token.trim().length === 0) {
    return { kind: "not_found" };
  }

  const base = baseUrlOverride?.trim() || BASE_URL;

  try {
    const response = await fetch(
      `${base}/api/membership/by-token/${encodeURIComponent(token)}`,
    );

    if (response.status === 404) {
      return { kind: "not_found" };
    }

    if (!response.ok) {
      console.warn(
        "[MembershipService] Resume state API returned status:",
        response.status,
      );
      return { kind: "unreachable" };
    }

    const raw: RawMembershipResponse = await response.json();
    const status = (raw.status || "").toLowerCase();

    // Pending: paid but invoice.paid not processed yet
    if (status === "pending" || status === "incomplete") {
      return { kind: "pending" };
    }

    // Active or cancelling: the membership is usable
    if (status === "active" || status === "cancelling") {
      const entitlements = raw.membership_entitlements || [];
      const availableEntitlements = entitlements.filter(
        (e) => e.status === "available",
      );
      const discountPct =
        raw.discount_percentage > 0
          ? Math.round(raw.discount_percentage * 100)
          : undefined;

      return {
        kind: "active",
        data: {
          id: raw.id,
          email: raw.email,
          first_name: raw.first_name,
          last_name: raw.last_name,
          plan_slug: raw.plan_slug,
          status: raw.status,
          discount_percentage: raw.discount_percentage,
          discount_fixed_value: raw.discount_fixed_value,
          membership_entitlements: entitlements,
          membershipDiscountPercentage: discountPct,
          membershipDiscountFixValue:
            raw.discount_fixed_value > 0 ? raw.discount_fixed_value : undefined,
          entitledProductIds: availableEntitlements.map(
            (e) => e.stripe_product_id,
          ),
          currentAvailableEntitlement:
            raw.current_available_entitlement ?? null,
          tosAcceptedAt: raw.tos_accepted_at ?? null,
          medicalConsentAcceptedAt: raw.medical_consent_accepted_at ?? null,
          flow: raw.flow ?? null,
        },
      };
    }

    // Any other status (cancelled, past_due, unpaid, etc.)
    return { kind: "inactive", status: raw.status };
  } catch (err) {
    console.error("[MembershipService] Resume state fetch failed:", err);
    return { kind: "unreachable" };
  }
}
